import type { TimetableStructure } from '@schooly/api';
import type { ValidRow, ValidationResult, XlsWorkbook } from '@schooly/components/xls-service';
import { mapRows, validate } from '@schooly/components/xls-service';

import { buildColumnMapping, buildTimetableSchema, GroupWithCode } from './buildTimetableSchema';
import type { TimetableImportMessages } from './useTimetableImportMessages';

export type ValidateTimetableParams = {
  workbook: XlsWorkbook;
  structure: TimetableStructure;
  groups: GroupWithCode[];
  roomPathMap: Map<string, string>;
  msg: TimetableImportMessages;
  sheetName?: string;
};

export type ValidationErrorReason = 'columns-missing' | 'columns-empty';

type ValidateTimetableResult =
  | { ok: true; result: ValidationResult }
  | { ok: false; reason: ValidationErrorReason };

const MIN_COLUMNS = 3;

function normalizeGroupCode(value: string): string {
  return value.toUpperCase();
}

export function validateTimetableFile(params: ValidateTimetableParams): ValidateTimetableResult {
  const { workbook, structure, groups, roomPathMap, msg, sheetName: selectedSheet } = params;

  const sheetName = selectedSheet ?? workbook.sheetNames[0];
  if (!sheetName) return { ok: false, reason: 'columns-missing' };

  const headers = workbook.getHeaders(sheetName);

  if (headers.length < MIN_COLUMNS) return { ok: false, reason: 'columns-missing' };

  const mapping = buildColumnMapping(headers);
  const rawRows = workbook.getRows(sheetName);

  if (!rawRows.length) return { ok: false, reason: 'columns-empty' };

  const rows = mapRows(rawRows, mapping).map((row) => ({
    ...row,
    group: typeof row.group === 'string' ? normalizeGroupCode(row.group) : row.group,
  }));

  const { schema, crossRules } = buildTimetableSchema({ msg, structure, groups, roomPathMap });
  const result = validate(rows, schema, crossRules);

  return { ok: true, result };
}

export function getImportableRows(result: ValidationResult): ValidRow[] {
  const warningOnlyRows = result.invalidRows.filter((r) =>
    r.issues.every((i) => i.type === 'warning'),
  );
  return [...result.validRows, ...warningOnlyRows];
}

export type ImportTimetableData = {
  result: ValidationResult;
  groups: GroupWithCode[];
  roomPathMap: Map<string, string>;
};

export type ImportSuccessData = {
  timetableId: string;
  importedRowsCount: number;
  notImportedRowsCount: number;
};

export type SelectSheetData = {
  workbook: XlsWorkbook;
  fileName: string;
};

export type ImportTimetableState =
  | { status: 'idle' }
  | { status: 'selectingSheet'; selectSheetData: SelectSheetData }
  | { status: 'validating' }
  | { status: 'validationError'; reason: ValidationErrorReason }
  | { status: 'reviewing'; data: ImportTimetableData }
  | { status: 'importing'; data: ImportTimetableData }
  | { status: 'success'; data: ImportSuccessData };

export type ImportTimetableAction =
  | { type: 'validate' }
  | { type: 'selectSheet'; data: SelectSheetData }
  | { type: 'review'; data: ImportTimetableData }
  | { type: 'import' }
  | { type: 'success'; data: ImportSuccessData }
  | { type: 'validationError'; reason: ValidationErrorReason }
  | { type: 'error' }
  | { type: 'reset' };

export const INITIAL_STATE: ImportTimetableState = { status: 'idle' };

export function hasImportData(
  state: ImportTimetableState,
): state is
  | { status: 'reviewing'; data: ImportTimetableData }
  | { status: 'importing'; data: ImportTimetableData } {
  return state.status === 'reviewing' || state.status === 'importing';
}

export function importTimetableReducer(
  state: ImportTimetableState,
  action: ImportTimetableAction,
): ImportTimetableState {
  switch (action.type) {
    case 'selectSheet':
      return { status: 'selectingSheet', selectSheetData: action.data };
    case 'validate':
      return { status: 'validating' };
    case 'validationError':
      return { status: 'validationError', reason: action.reason };
    case 'review':
      return { status: 'reviewing', data: action.data };
    case 'import':
      if (hasImportData(state)) return { status: 'importing', data: state.data };
      return state;
    case 'success':
      return { status: 'success', data: action.data };
    case 'error':
      if (hasImportData(state)) return { status: 'reviewing', data: state.data };
      return INITIAL_STATE;
    case 'reset':
      return INITIAL_STATE;
    default:
      return state;
  }
}
